import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Env,
  ServiceEntry,
  extractReleasableServices,
  resolveDepOrder,
} from './promoteUtils';
import { PromoteModal } from './PromoteModal';
import { StepSelect, VersionMap } from './steps/StepSelect';
import { StepNotes, NotesMode, NotesPhase, PerServiceFetch } from './steps/StepNotes';
import { StepReview, PublishState } from './steps/StepReview';
import { StepConfirm } from './steps/StepConfirm';
import { StepRollout } from './steps/StepRollout';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sourceTargetTitle: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: -0.4,
    margin: 0,
  },
  envMono: {
    fontFamily: 'monospace',
    fontSize: 20,
    fontWeight: 600,
  },
  envArrow: {
    color: theme.palette.text.secondary,
    margin: `0 ${theme.spacing(1)}px`,
  },
  subtitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  stepper: {
    background: 'transparent',
    padding: 0,
    marginBottom: theme.spacing(2),
  },
}));

type StepId = 'select' | 'notes' | 'review' | 'confirm' | 'rollout';

interface StepDef {
  id: StepId;
  label: string;
  sub: string;
}

const ALL_STEPS: StepDef[] = [
  { id: 'select', label: 'Select', sub: 'Services' },
  { id: 'notes', label: 'Release notes', sub: 'Generate?' },
  { id: 'review', label: 'Review', sub: 'Edit & publish' },
  { id: 'confirm', label: 'Confirm', sub: 'Review plan' },
  { id: 'rollout', label: 'Rollout', sub: 'In progress' },
];

export interface PromotionWizardProps {
  onExit: () => void;
}

export const PromotionWizard = ({ onExit }: PromotionWizardProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);

  // Promote modal first — user must pick source/target before we render wizard
  const [modalOpen, setModalOpen] = useState(true);
  const [envs, setEnvs] = useState<{ source: Env; target: Env } | null>(null);

  const [services, setServices] = useState<ServiceEntry[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [versions, setVersions] = useState<VersionMap>({});

  // Wizard state
  const [step, setStep] = useState(0);
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set());
  const [notesMode, setNotesMode] = useState<NotesMode>(null);
  const [notesPhase, setNotesPhase] = useState<NotesPhase>('idle');
  const [perServiceFetch, setPerServiceFetch] = useState<PerServiceFetch[]>([]);
  const [markdown, setMarkdown] = useState('');
  const [bundleVersion, setBundleVersion] = useState('');
  const [publishState, setPublishState] = useState<PublishState>('idle');
  const [publishError, setPublishError] = useState<string | null>(null);
  const [rolloutDone, setRolloutDone] = useState(false);

  const hasNotes = notesMode === 'generate' || notesMode === 'existing';

  // Load catalog services on mount
  useEffect(() => {
    catalogApi
      .getEntities({ filter: { kind: 'Component' } })
      .then(({ items }) => {
        const list = extractReleasableServices(items);
        setServices(list);
        // Default-select all releasable services
        setSelectedNames(new Set(list.map((s) => s.name)));
      })
      .finally(() => setLoadingServices(false));
  }, [catalogApi]);

  // Probe versions for every service × env once services are loaded
  useEffect(() => {
    if (services.length === 0) return;
    let cancelled = false;
    (async () => {
      const baseUrl = await discoveryApi.getBaseUrl('service-versions');
      const next: VersionMap = {};
      await Promise.all(
        services.flatMap((svc) =>
          (['dev', 'stage', 'prod'] as Env[]).map(async (env) => {
            const url = svc.versionUrls[env];
            if (!url) return;
            try {
              const res = await backstageFetch(
                `${baseUrl}/probe?url=${encodeURIComponent(url)}`,
              );
              if (res.ok) {
                const data = await res.json();
                const v = data.version ?? data.Version;
                if (typeof v === 'string') {
                  if (!next[svc.name]) next[svc.name] = {};
                  next[svc.name]![env] = v;
                }
              }
            } catch {
              /* skip */
            }
          }),
        ),
      );
      if (!cancelled) setVersions(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [backstageFetch, discoveryApi, services]);

  // Compute effective steps — show Review only when generating new notes.
  // Skipping notes or reusing an existing bundle doesn't require the review/publish step.
  const effectiveSteps: StepDef[] =
    notesMode === 'generate' ? ALL_STEPS : ALL_STEPS.filter((s) => s.id !== 'review');
  const currentStep = effectiveSteps[step];

  const selectedServices = services.filter((s) => selectedNames.has(s.name));

  const canForward = useCallback((): boolean => {
    if (!currentStep) return false;
    switch (currentStep.id) {
      case 'select':
        return selectedNames.size > 0 && !!envs;
      case 'notes':
        if (notesMode === null) return false;
        if (notesMode === 'skip') return true;
        // Both 'generate' and 'existing' require the markdown fetch/load to finish
        return notesPhase === 'done';
      case 'review':
        return publishState === 'done';
      case 'confirm':
        return true;
      case 'rollout':
        return rolloutDone;
    }
  }, [currentStep, envs, notesMode, notesPhase, publishState, rolloutDone, selectedNames.size]);

  const primaryLabel = (() => {
    if (!currentStep) return 'Continue';
    if (currentStep.id === 'review') return publishState === 'done' ? 'Continue' : 'Publish first';
    if (currentStep.id === 'confirm') return 'Start rollout';
    if (currentStep.id === 'rollout') return rolloutDone ? 'Done' : 'Running…';
    return 'Continue';
  })();

  const onPrimary = () => {
    if (!currentStep) return;
    if (currentStep.id === 'rollout') {
      if (rolloutDone) onExit();
      return;
    }
    if (step < effectiveSteps.length - 1) setStep(step + 1);
  };

  // The dep-order services, memoized for confirm/rollout
  const orderedServices = resolveDepOrder(services, selectedNames);

  // Compute note counts for StepReview
  const noteCounts: Record<string, number> = {};
  for (const p of perServiceFetch) noteCounts[p.service] = p.count ?? 0;

  if (!envs) {
    return (
      <PromoteModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          onExit();
        }}
        onConfirm={(source, target) => {
          setEnvs({ source, target });
          setModalOpen(false);
        }}
      />
    );
  }

  if (loadingServices) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <div className={classes.header}>
        <div>
          <Typography
            variant="overline"
            color="textSecondary"
            style={{ letterSpacing: 0.8 }}
          >
            Release pipeline
          </Typography>
          <h1 className={classes.sourceTargetTitle}>
            Promote <span className={classes.envMono}>{envs.source}</span>
            <span className={classes.envArrow}>→</span>
            <span className={classes.envMono} style={{ color: '#8b5cf6' }}>
              {envs.target}
            </span>
          </h1>
          <div className={classes.subtitle}>
            {services.length} releasable services
          </div>
        </div>
        <Button
          size="small"
          startIcon={<CloseIcon />}
          onClick={onExit}
          disabled={currentStep?.id === 'rollout' && !rolloutDone}
        >
          Cancel
        </Button>
      </div>

      <Stepper activeStep={step} alternativeLabel className={classes.stepper}>
        {effectiveSteps.map((s) => (
          <Step key={s.id}>
            <StepLabel>
              {s.label}
              <Typography variant="caption" color="textSecondary" display="block">
                {s.sub}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box>
        {currentStep?.id === 'select' && (
          <StepSelect
            services={services}
            versions={versions}
            selectedNames={selectedNames}
            setSelectedNames={setSelectedNames}
            sourceEnv={envs.source}
            targetEnv={envs.target}
          />
        )}
        {currentStep?.id === 'notes' && (
          <StepNotes
            selectedServices={selectedServices}
            versions={versions}
            sourceEnv={envs.source}
            targetEnv={envs.target}
            notesMode={notesMode}
            setNotesMode={setNotesMode}
            phase={notesPhase}
            setPhase={setNotesPhase}
            perService={perServiceFetch}
            setPerService={setPerServiceFetch}
            markdown={markdown}
            setMarkdown={setMarkdown}
            bundleVersion={bundleVersion}
            setBundleVersion={setBundleVersion}
          />
        )}
        {currentStep?.id === 'review' && (
          <StepReview
            markdown={markdown}
            setMarkdown={setMarkdown}
            bundleVersion={bundleVersion}
            setBundleVersion={setBundleVersion}
            publishState={publishState}
            setPublishState={setPublishState}
            publishError={publishError}
            setPublishError={setPublishError}
            selectedServices={selectedServices}
            noteCounts={noteCounts}
          />
        )}
        {currentStep?.id === 'confirm' && (
          <StepConfirm
            selectedServices={selectedServices}
            versions={versions}
            sourceEnv={envs.source}
            targetEnv={envs.target}
            hasNotes={hasNotes}
            notesMode={notesMode}
            bundleVersion={bundleVersion || null}
            owner={null}
          />
        )}
        {currentStep?.id === 'rollout' && (
          <StepRollout
            orderedServices={orderedServices}
            versions={versions}
            sourceEnv={envs.source}
            targetEnv={envs.target}
            hasNotes={hasNotes}
            bundleVersion={bundleVersion}
            onDone={({ success }) => {
              if (success) setRolloutDone(true);
            }}
          />
        )}
      </Box>

      <div className={classes.footer}>
        <Typography variant="caption" color="textSecondary">
          Step {step + 1} of {effectiveSteps.length}
        </Typography>
        <Box display="flex" gridGap={8}>
          {step > 0 && currentStep?.id !== 'rollout' && (
            <Button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={onPrimary}
            disabled={!canForward()}
            endIcon={currentStep?.id !== 'rollout' ? <ArrowForwardIcon /> : undefined}
          >
            {primaryLabel}
          </Button>
        </Box>
      </div>
    </Box>
  );
};
